/* ═══════════════════════════════════════════════════════════════════════════
   HENNEY BEAR LONDON — theme behaviour
   Vanilla JS: mobile menu, announcement rotation, AJAX cart drawer,
   product variant switching, quantity steppers, sort.
   ═══════════════════════════════════════════════════════════════════════════ */
(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
      style: 'currency',
      currency: window.Henney?.currency || 'EUR',
    }).format(cents / 100);

  /* ── Mobile menu ─────────────────────────────────────────────────────── */
  function initMobileMenu() {
    const toggle = $('[data-menu-toggle]');
    const panel = $('[data-mobile-menu]');
    if (!toggle || !panel) return;
    toggle.addEventListener('click', () => {
      const open = panel.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
    });
  }

  /* ── Mega menu ───────────────────────────────────────────────────────── */
  // Hover/focus open it via CSS; JS syncs aria-expanded, lets touch devices
  // open the panel on first tap instead of navigating, and closes on Escape.
  function initMegaMenu() {
    const items = $$('[data-mega]');
    if (!items.length) return;
    const noHover = window.matchMedia('(hover: none)');
    const setOpen = (item, open) => {
      item.classList.toggle('is-open', open);
      $('[data-mega-trigger]', item).setAttribute('aria-expanded', String(open));
    };
    const closeAll = (except) => items.forEach((i) => i !== except && setOpen(i, false));

    items.forEach((item) => {
      const trigger = $('[data-mega-trigger]', item);
      item.addEventListener('mouseenter', () => {
        item.classList.remove('is-closed');
        trigger.setAttribute('aria-expanded', 'true');
      });
      item.addEventListener('mouseleave', () => setOpen(item, false));
      item.addEventListener('focusin', () => trigger.setAttribute('aria-expanded', 'true'));
      item.addEventListener('focusout', (e) => {
        if (item.contains(e.relatedTarget)) return;
        setOpen(item, false);
        item.classList.remove('is-closed');
      });
      trigger.addEventListener('click', (e) => {
        if (noHover.matches && !item.classList.contains('is-open')) {
          e.preventDefault();
          closeAll(item);
          setOpen(item, true);
        }
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const open = items.find((i) => i.contains(document.activeElement) || i.classList.contains('is-open'));
      if (!open) return;
      setOpen(open, false);
      open.classList.add('is-closed');
      $('[data-mega-trigger]', open).focus();
    });
    document.addEventListener('click', (e) => {
      if (!e.target.closest('[data-mega]')) closeAll();
    });
  }

  /* ── Announcement rotation ───────────────────────────────────────────── */
  function initAnnouncements() {
    const wrap = $('[data-announcements]');
    if (!wrap) return;
    const items = $$('[data-announcement]', wrap);
    if (items.length < 2) return;
    let i = 0;
    setInterval(() => {
      items[i].hidden = true;
      i = (i + 1) % items.length;
      items[i].hidden = false;
      items[i].classList.remove('animate-fade-up');
      void items[i].offsetWidth;
      items[i].classList.add('animate-fade-up');
    }, 4500);
  }

  /* ── Cart drawer ─────────────────────────────────────────────────────── */
  const Cart = {
    drawer: null,
    init() {
      this.drawer = $('[data-cart-drawer]');
      $$('[data-cart-open]').forEach((b) =>
        b.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        })
      );
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
      document.addEventListener('click', (e) => {
        const close = e.target.closest('[data-cart-close]');
        if (close) this.close();
        const add = e.target.closest('[data-add-to-cart]');
        if (add) {
          e.preventDefault();
          this.add(add);
        }
        const remove = e.target.closest('[data-line-remove]');
        if (remove) {
          e.preventDefault();
          this.change(remove.dataset.lineRemove, 0);
        }
        const inc = e.target.closest('[data-line-inc]');
        if (inc) this.change(inc.dataset.lineInc, parseInt(inc.dataset.qty, 10) + 1);
        const dec = e.target.closest('[data-line-dec]');
        if (dec) this.change(dec.dataset.lineDec, parseInt(dec.dataset.qty, 10) - 1);
      });
    },
    open() {
      if (!this.drawer || this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('drawer--instant');
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('cart-open');
    },
    close() {
      if (!this.drawer || !this.drawer.classList.contains('is-open')) return;
      this.drawer.classList.remove('is-open', 'drawer--instant');
      this.drawer.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('cart-open');
    },
    async add(btn) {
      const id = btn.dataset.variantId;
      if (!id) return;
      const qty = parseInt(btn.dataset.quantity || '1', 10);
      btn.classList.add('is-loading');
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(id), quantity: qty }),
        });
        if (!res.ok) throw new Error('add failed');
        await this.refresh();
        this.open();
      } catch (err) {
        console.error(err);
      } finally {
        btn.classList.remove('is-loading');
      }
    },
    async change(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, quantity) }),
        });
        if (!res.ok) throw new Error('change failed');
        await this.refresh();
      } catch (err) {
        console.error(err);
      }
    },
    async refresh() {
      try {
        const res = await fetch('/?sections=cart-drawer');
        const data = await res.json();
        if (data['cart-drawer']) {
          const tmp = document.createElement('div');
          tmp.innerHTML = data['cart-drawer'];
          const fresh = tmp.querySelector('[data-cart-drawer]');
          const wasOpen = this.drawer && this.drawer.classList.contains('is-open');
          if (fresh && this.drawer) {
            if (wasOpen) this.drawer.classList.add('drawer--instant');
            this.drawer.innerHTML = fresh.innerHTML;
            if (wasOpen) {
              this.drawer.classList.add('is-open');
              void this.drawer.offsetWidth;
              this.drawer.classList.remove('drawer--instant');
            }
          }
        }
        await this.refreshCartPage();
        const cart = await (await fetch('/cart.js')).json();
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.hidden = cart.item_count === 0;
        });
      } catch (err) {
        console.error(err);
      }
    },
    /* The cart page renders its own line items, so it has to be re-rendered
       alongside the drawer when a quantity changes there. */
    async refreshCartPage() {
      const current = $('[data-cart-page]');
      if (!current) return;
      const res = await fetch('/cart?sections=main-cart');
      const data = await res.json();
      if (!data['main-cart']) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = data['main-cart'];
      const fresh = tmp.querySelector('[data-cart-page]');
      if (fresh) current.innerHTML = fresh.innerHTML;
    },
  };

  /* ── Product variant switching ───────────────────────────────────────── */
  function initProductForm() {
    const root = $('[data-product]');
    if (!root) return;
    let variants = [];
    try {
      variants = JSON.parse($('[data-variants]', root)?.textContent || '[]');
    } catch (e) {
      variants = [];
    }
    const stage = $('[data-pdp-image]', root);
    const setActiveThumb = (mediaId) => {
      $$('[data-pdp-thumb]', root).forEach((t) => t.classList.toggle('is-active', t.dataset.mediaId === mediaId));
    };
    const priceEl = $('[data-pdp-price]', root);
    const compareEl = $('[data-pdp-compare]', root);
    const idInput = $('[data-variant-id]', root);
    const addBtn = $('[data-add-to-cart]', root);
    const optName = $('[data-opt-name]', root);
    const blurbEl = $('[data-opt-blurb]', root);
    const qtyInput = $('[data-qty-input]', root);
    const buyRow = $('.pdp__buy', root);
    const variantBadge = $('[data-pdp-variant-badge]', root);
    const stockEl = $('[data-pdp-stock]', root);
    const countEl = $('[data-pdp-count]', root);
    const skuEl = $('[data-pdp-sku]', root);
    let unitPrice = Number(addBtn?.dataset.unitPrice || 0);
    let maxQty = Number(qtyInput?.max || 99);

    function stockState(variant) {
      const tracked = Boolean(variant.inventory_management);
      const qty = Number(variant.inventory_quantity) || 0;
      const available = Boolean(variant.available);
      const lastUnit = available && tracked && qty === 1;
      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      let cap = 99;
      if (tracked && qty > 0) cap = qty;
      if (!available) cap = 1;
      return { available, lastUnit, oos: !available, tracked, qty, onSale, maxQty: cap };
    }

    function setHidden(el, hide) {
      if (!el) return;
      el.hidden = hide;
    }

    function unitsLabel(n) {
      if (!countEl) return '';
      const tpl = n === 1 ? countEl.dataset.labelOne : countEl.dataset.labelOther;
      return (tpl || '').replace('[count]', String(n));
    }

    function applyStock(variant) {
      const s = stockState(variant);
      maxQty = s.maxQty;
      if (variantBadge) {
        variantBadge.classList.remove('badge--oos', 'badge--last', 'badge--sale');
        if (s.oos) {
          variantBadge.textContent = variantBadge.dataset.labelOos || '';
          variantBadge.classList.add('badge--oos');
          setHidden(variantBadge, false);
        } else if (s.lastUnit) {
          variantBadge.textContent = variantBadge.dataset.labelLast || '';
          variantBadge.classList.add('badge--last');
          setHidden(variantBadge, false);
        } else if (s.onSale) {
          variantBadge.textContent = variantBadge.dataset.labelSale || '';
          variantBadge.classList.add('badge--sale');
          setHidden(variantBadge, false);
        } else {
          variantBadge.textContent = '';
          setHidden(variantBadge, true);
        }
      }
      if (skuEl) {
        const sku = variant.sku ? String(variant.sku).trim() : '';
        skuEl.textContent = sku ? `Ref. ${sku}` : '';
        setHidden(skuEl, !sku);
      }
      if (countEl) {
        if (!s.tracked) {
          countEl.textContent = '';
          setHidden(countEl, true);
        } else {
          const shown = s.available ? s.qty : 0;
          countEl.textContent = unitsLabel(shown);
          setHidden(countEl, false);
        }
      }
      if (stockEl) {
        stockEl.classList.toggle('pdp__stock--last', s.lastUnit);
        stockEl.classList.toggle('pdp__stock--oos', s.oos);
        if (s.oos) {
          stockEl.textContent = stockEl.dataset.labelOos || '';
          setHidden(stockEl, false);
        } else if (s.lastUnit) {
          stockEl.textContent = stockEl.dataset.labelLast || '';
          setHidden(stockEl, false);
        } else {
          stockEl.textContent = '';
          setHidden(stockEl, true);
        }
      }
      if (buyRow) buyRow.classList.toggle('is-oos', s.oos);
      if (qtyInput) {
        qtyInput.max = String(s.maxQty);
        const currentQty = Math.max(1, parseInt(qtyInput.value || '1', 10));
        if (currentQty > s.maxQty) qtyInput.value = String(s.maxQty);
      }
      if (addBtn) {
        addBtn.disabled = !s.available;
        updateAddLabel(s.available);
      }
    }

    /* Proto: "Añadir a la bolsa · {price * qty}" */
    function updateAddLabel(available) {
      if (!addBtn) return;
      const label = addBtn.querySelector('[data-add-label]');
      if (!label) return;
      const isAvailable = available !== undefined ? available : !addBtn.disabled;
      if (!isAvailable) {
        label.textContent = addBtn.dataset.labelSold || '';
        return;
      }
      const qty = Math.max(1, parseInt(qtyInput?.value || addBtn.dataset.quantity || '1', 10));
      const total = unitPrice * qty;
      label.textContent = `${addBtn.dataset.labelAdd || ''} · ${money(total)}`;
    }

    function select(variant, swatch) {
      if (!variant) return;
      if (idInput) idInput.value = variant.id;
      if (addBtn) addBtn.dataset.variantId = variant.id;
      unitPrice = Number(variant.price) || 0;
      if (addBtn) addBtn.dataset.unitPrice = String(unitPrice);
      if (priceEl) priceEl.textContent = money(variant.price);
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compareEl.textContent = money(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }
      if (variant.featured_image && stage) stage.src = variant.featured_image.src || variant.featured_image;
      if (variant.featured_media_id) setActiveThumb(String(variant.featured_media_id));
      if (optName) optName.textContent = variant.title;
      applyStock(variant);
      if (swatch) {
        $$('[data-swatch]', root).forEach((s) => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
        if (blurbEl && swatch.dataset.blurb !== undefined) blurbEl.textContent = swatch.dataset.blurb;
      }
    }

    $$('[data-swatch]', root).forEach((swatch) => {
      swatch.addEventListener('click', (e) => {
        e.preventDefault();
        const variant = variants.find((v) => String(v.id) === String(swatch.dataset.variantId));
        select(variant, swatch);
      });
    });

    $$('[data-pdp-thumb]', root).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (stage) stage.src = thumb.dataset.src;
        setActiveThumb(thumb.dataset.mediaId);
      });
    });

    $('[data-qty-dec]', root)?.addEventListener('click', () => {
      qtyInput.value = Math.max(1, parseInt(qtyInput.value, 10) - 1);
      syncQty();
    });
    $('[data-qty-inc]', root)?.addEventListener('click', () => {
      qtyInput.value = Math.min(maxQty, parseInt(qtyInput.value, 10) + 1);
      syncQty();
    });
    qtyInput?.addEventListener('change', () => {
      let next = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      if (next > maxQty) next = maxQty;
      qtyInput.value = String(next);
      syncQty();
    });
    function syncQty() {
      if (addBtn && qtyInput) addBtn.dataset.quantity = qtyInput.value;
      updateAddLabel();
    }
  }

  /* ── Card swatch hover (collection cards) ────────────────────────────── */
  function initCardSwatches() {
    $$('[data-card]').forEach((card) => {
      const mainImg = $('.card__img--main', card);
      if (!mainImg) return;
      const hoverImg = $('.card__img--hover', card);
      const addBtn = $('[data-add-to-cart]', card);

      const setImg = (img, url) => {
        if (!img || !url) return;
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = url;
      };

      $$('[data-card-swatch]', card).forEach((sw) => {
        const apply = () => {
          if (sw.dataset.image) setImg(mainImg, sw.dataset.image);
          if (hoverImg) {
            if (sw.dataset.hoverImage) setImg(hoverImg, sw.dataset.hoverImage);
            else setImg(hoverImg, sw.dataset.image || mainImg.src);
          }
          if (addBtn && sw.dataset.variantId) addBtn.dataset.variantId = sw.dataset.variantId;
          $$('[data-card-swatch]', card).forEach((s) => {
            s.classList.remove('is-active');
            s.setAttribute('aria-pressed', 'false');
          });
          sw.classList.add('is-active');
          sw.setAttribute('aria-pressed', 'true');
        };
        sw.addEventListener('mouseenter', apply);
        sw.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          apply();
        });
      });
    });
  }

  /* ── Predictive search ───────────────────────────────────────────────── */
  function initSearch() {
    const dialog = $('[data-search-dialog]');
    if (!dialog) return;
    const input = $('[data-search-input]', dialog);
    const output = $('[data-search-output]', dialog);

    $$('[data-search-open]').forEach((btn) =>
      btn.addEventListener('click', () => {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        input?.focus();
      })
    );
    $$('[data-search-close]', dialog).forEach((btn) => btn.addEventListener('click', () => dialog.close()));
    dialog.addEventListener('close', () => {
      if (output) output.innerHTML = '';
      if (input) {
        input.value = '';
        input.setAttribute('aria-expanded', 'false');
      }
    });

    if (!input || !output) return;

    let timer;
    let controller;
    input.addEventListener('input', () => {
      const term = input.value.trim();
      clearTimeout(timer);
      controller?.abort();
      if (term.length < 2) {
        output.innerHTML = '';
        input.setAttribute('aria-expanded', 'false');
        return;
      }
      timer = setTimeout(async () => {
        controller = new AbortController();
        const url =
          '/search/suggest?q=' +
          encodeURIComponent(term) +
          '&resources[type]=product,collection&resources[limit]=6' +
          '&section_id=predictive-search';
        try {
          const res = await fetch(url, { signal: controller.signal });
          if (!res.ok) throw new Error('search failed');
          const html = await res.text();
          const tmp = document.createElement('div');
          tmp.innerHTML = html;
          const fresh = tmp.querySelector('[data-psearch-results]');
          output.innerHTML = fresh ? fresh.outerHTML : '';
          input.setAttribute('aria-expanded', String(Boolean(fresh)));
        } catch (err) {
          if (err.name !== 'AbortError') console.error(err);
        }
      }, 250);
    });
  }

  /* ── Sort select ─────────────────────────────────────────────────────── */
  function initSort() {
    const sel = $('[data-sort]');
    if (!sel) return;
    sel.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ── Mobile filter toggle ────────────────────────────────────────────── */
  function initFilterToggle() {
    const toggle = $('[data-filter-toggle]');
    const panel = $('[data-filter-panel]');
    if (!toggle || !panel) return;
    toggle.addEventListener('click', () => panel.classList.toggle('is-open'));
  }

  document.addEventListener('DOMContentLoaded', () => {
    initMobileMenu();
    initMegaMenu();
    initAnnouncements();
    Cart.init();
    initProductForm();
    initSearch();
    initCardSwatches();
    initSort();
    initFilterToggle();
  });
})();
